import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, Check, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { API, formatDate } from "./utils";

export const NewClientForm = ({ onBack, onCreated, currentDate }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    client_name: "",
    client_prenom: "",
    phone: "",
    address: "",
    email: "",
    type_logement: "",
    date: formatDate(currentDate),
    start_time: "10:00",
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.client_name.trim()) { toast.error("Nombre obligatorio"); return; }
    if (!form.date || !form.start_time) { toast.error("Fecha y hora obligatorias"); return; }
    const hour = parseInt(form.start_time.split(":")[0], 10);
    if (hour < 7 || hour >= 20) { toast.error("Las citas deben estar entre las 07:00 y las 20:00"); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem("tech_token");
      await axios.post(`${API}/appointments`, {
        ...form,
        end_time: form.start_time,
        source: "Autre",
        status: "Instalación programada",
        color: "#3b82f6",
        created_by_tech: true,
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Cliente creado con éxito");
      onCreated();
    } catch {
      toast.error("Error al crear cliente");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="new-client-form">
      <div className="bg-slate-800 text-white px-4 py-3 flex items-center gap-3">
        <button onClick={onBack} className="p-1" data-testid="new-client-back">
          <ChevronLeft size={22} />
        </button>
        <h1 className="text-lg font-bold">Nuevo Cliente</h1>
      </div>
      <div className="px-4 py-4 space-y-3">
        <Card>
          <CardContent className="pt-4 space-y-3">
            <h2 className="text-sm font-bold text-slate-700">Información del cliente</h2>
            <div>
              <label className="text-xs text-slate-500">Nombre y Apellido *</label>
              <Input value={form.client_name} onChange={e => set("client_name", e.target.value)} placeholder="Nombre y Apellido" data-testid="new-client-name" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Teléfono</label>
              <Input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="Teléfono" type="tel" data-testid="new-client-phone" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Email</label>
              <Input value={form.email} onChange={e => set("email", e.target.value)} placeholder="Email" type="email" data-testid="new-client-email" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Dirección</label>
              <Input value={form.address} onChange={e => set("address", e.target.value)} placeholder="Calle, número" data-testid="new-client-address" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500">Ciudad</label>
                <Input value={form.city || ""} onChange={e => set("city", e.target.value)} placeholder="Ciudad" data-testid="new-client-city" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Código Postal</label>
                <Input value={form.postal_code || ""} onChange={e => set("postal_code", e.target.value)} placeholder="CP" data-testid="new-client-cp" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 space-y-3">
            <h2 className="text-sm font-bold text-slate-700">Tipo de vivienda</h2>
            <div className="grid grid-cols-3 gap-2">
              {[["apartamento","Apartamento"],["casa","Casa"],["local","Local"]].map(([val, label]) => (
                <button key={val} onClick={() => set("type_logement", val)}
                  className={`py-2.5 rounded-lg text-sm font-medium border transition-all ${form.type_logement === val ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-200"}`}
                  data-testid={`type-${val}`}>
                  {label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 space-y-3">
            <h2 className="text-sm font-bold text-slate-700">Fecha y hora</h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500">Fecha *</label>
                <Input type="date" value={form.date} onChange={e => set("date", e.target.value)} data-testid="new-client-date" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Hora *</label>
                <Input type="time" value={form.start_time} onChange={e => set("start_time", e.target.value)} data-testid="new-client-time" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Button onClick={handleSubmit} disabled={saving}
          className="w-full h-14 text-lg font-semibold bg-emerald-600 hover:bg-emerald-700"
          data-testid="new-client-submit">
          {saving ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Creando...</> : <><Check size={22} className="mr-2" /> Crear cliente</>}
        </Button>
      </div>
    </div>
  );
};
